import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, BarChart3, TrendingUp, TrendingDown, Minus, Star, Trophy,
  BookOpen, Calculator, Pencil, FlaskConical, Shield, Globe, Target,
  Award, Clock, Zap, ChevronRight, Lightbulb, Gamepad2, ClipboardCheck,
  Loader2, Calendar, Activity
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';

const ParentProgressReport = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [childId]);

  const fetchAllData = async () => {
    try {
      const [overviewRes, progressRes, recsRes] = await Promise.all([
        api.get(`/reports/child/${childId}/overview`),
        api.get(`/reports/child/${childId}/progress-over-time?days=30`),
        api.get(`/reports/child/${childId}/recommendations`)
      ]);
      
      setOverview(overviewRes.data);
      setProgressData(progressRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load progress report');
    } finally {
      setLoading(false);
    }
  };

  const iconMap = {
    'shield': Shield,
    'clipboard': ClipboardCheck,
    'calculator': Calculator,
    'target': Target,
    'pencil': Pencil,
    'globe': Globe,
    'gamepad': Gamepad2,
  };

  const subjectIcons = {
    reading: BookOpen,
    math: Calculator,
    writing: Pencil,
    science: FlaskConical,
  };

  const subjectColors = {
    reading: 'emerald',
    math: 'red',
    writing: 'cyan',
    science: 'purple',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading progress report...</p>
        </div>
      </div>
    );
  }

  const trendIcon = progressData?.trend === 'improving' ? TrendingUp : 
                    progressData?.trend === 'needs_attention' ? TrendingDown : Minus;
  const trendColor = progressData?.trend === 'improving' ? 'text-emerald-500' : 
                     progressData?.trend === 'needs_attention' ? 'text-red-500' : 'text-slate-500';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 px-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/dashboard" data-testid="back-to-dashboard">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Progress Report</h1>
              <p className="text-xs text-white/80">{overview?.child_name}</p>
            </div>
          </div>
          
          <Badge className="bg-white/20 text-white border-0">
            <Star className="w-3 h-3 mr-1 fill-amber-300 text-amber-300" />
            {overview?.summary?.total_xp || 0} XP
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-4 text-center">
              <Zap className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <p className="text-2xl font-bold text-amber-700">{overview?.summary?.total_xp || 0}</p>
              <p className="text-xs text-amber-600">Total XP</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="text-2xl font-bold text-emerald-700">{overview?.summary?.badges_earned || 0}</p>
              <p className="text-xs text-emerald-600">Badges</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-4 text-center">
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold text-blue-700">{overview?.summary?.lessons_completed || 0}</p>
              <p className="text-xs text-blue-600">Lessons</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold text-purple-700">{overview?.summary?.total_time_minutes || 0}</p>
              <p className="text-xs text-purple-600">Minutes</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md bg-gradient-to-br from-slate-50 to-gray-100">
            <CardContent className="p-4 text-center">
              <Activity className="w-8 h-8 mx-auto mb-2 text-slate-500" />
              <p className="text-2xl font-bold text-slate-700">Level {overview?.summary?.overall_level || 1}</p>
              <p className="text-xs text-slate-600">Average</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-white shadow-md">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="progress">Progress</TabsTrigger>
            <TabsTrigger value="subjects">Subjects</TabsTrigger>
            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Progress Chart */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-indigo-500" />
                      Learning Activity (Last 30 Days)
                    </CardTitle>
                    <CardDescription>Daily sessions and scores</CardDescription>
                  </div>
                  <div className={`flex items-center gap-2 ${trendColor}`}>
                    {React.createElement(trendIcon, { className: "w-5 h-5" })}
                    <span className="text-sm font-medium capitalize">
                      {progressData?.trend?.replace('_', ' ') || 'Loading'}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {progressData?.chart_data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={progressData.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(value, name) => [value, name === 'average_score' ? 'Avg Score' : name === 'xp_earned' ? 'XP Earned' : name]}
                      />
                      <Area type="monotone" dataKey="average_score" stroke="#6366f1" fill="#c7d2fe" name="Avg Score" />
                      <Area type="monotone" dataKey="xp_earned" stroke="#10b981" fill="#d1fae5" name="XP" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-400">
                    <p>No activity data yet. Start learning to see progress!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {overview?.recent_activity?.length > 0 ? (
                  <div className="space-y-3">
                    {overview.recent_activity.map((activity, idx) => {
                      const SubjectIcon = subjectIcons[activity.subject] || BookOpen;
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl bg-${subjectColors[activity.subject] || 'slate'}-100 flex items-center justify-center`}>
                              <SubjectIcon className={`w-5 h-5 text-${subjectColors[activity.subject] || 'slate'}-600`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{activity.lesson}</p>
                              <p className="text-xs text-slate-500">{activity.subject} • {new Date(activity.date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {activity.score !== null && (
                              <p className={`font-semibold ${activity.score >= 80 ? 'text-emerald-600' : activity.score >= 60 ? 'text-amber-600' : 'text-red-500'}`}>
                                {activity.score}%
                              </p>
                            )}
                            <p className="text-xs text-amber-600">+{activity.xp_earned} XP</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No recent activity</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROGRESS TAB */}
          <TabsContent value="progress" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Sessions Over Time */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Sessions Per Day</CardTitle>
                </CardHeader>
                <CardContent>
                  {progressData?.chart_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={progressData.chart_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="sessions" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-slate-400">
                      No data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats Summary */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>30-Day Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-xl">
                    <span className="text-indigo-700">Total Sessions</span>
                    <span className="font-bold text-indigo-800">{progressData?.total_sessions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                    <span className="text-emerald-700">Active Days</span>
                    <span className="font-bold text-emerald-800">{progressData?.active_days || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                    <span className="text-amber-700">Consistency</span>
                    <span className="font-bold text-amber-800">
                      {progressData?.active_days ? Math.round((progressData.active_days / 30) * 100) : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Safety Status */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-500" />
                  Digital Safety Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl ${overview?.safety?.lesson_completed ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <div className="flex items-center gap-3">
                      {overview?.safety?.lesson_completed ? (
                        <Trophy className="w-8 h-8 text-emerald-500" />
                      ) : (
                        <Shield className="w-8 h-8 text-amber-500" />
                      )}
                      <div>
                        <p className="font-semibold text-slate-800">Safety Lesson</p>
                        <p className={`text-sm ${overview?.safety?.lesson_completed ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {overview?.safety?.lesson_completed ? 'Completed!' : 'Not yet completed'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-cyan-50 border border-cyan-200">
                    <div className="flex items-center gap-3">
                      <Gamepad2 className="w-8 h-8 text-cyan-500" />
                      <div>
                        <p className="font-semibold text-slate-800">Safety Games</p>
                        <p className="text-sm text-cyan-600">
                          {overview?.safety?.games_completed || 0} of {overview?.safety?.total_games || 4} completed
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUBJECTS TAB */}
          <TabsContent value="subjects" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {['reading', 'math', 'writing', 'science'].map(subject => {
                const SubjectIcon = subjectIcons[subject];
                const color = subjectColors[subject];
                const level = overview?.subject_progress?.levels?.[subject] || 1;
                const mastery = overview?.subject_progress?.mastery?.[subject] || {};
                
                return (
                  <Card key={subject} className="border-0 shadow-lg overflow-hidden">
                    <div className={`bg-gradient-to-r from-${color}-400 to-${color}-500 p-4 text-white`}>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                          <SubjectIcon className="w-7 h-7" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg capitalize">{subject}</h3>
                          <p className="text-white/80 text-sm">Level {level}</p>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4 space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-500">Average Score</span>
                          <span className="font-semibold text-slate-700">{mastery.average || 0}%</span>
                        </div>
                        <Progress value={mastery.average || 0} className="h-2" />
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div className="p-2 bg-slate-50 rounded-lg">
                          <p className="text-lg font-bold text-slate-700">{mastery.highest || 0}%</p>
                          <p className="text-xs text-slate-500">Best Score</p>
                        </div>
                        <div className="p-2 bg-slate-50 rounded-lg">
                          <p className="text-lg font-bold text-slate-700">{mastery.sessions || 0}</p>
                          <p className="text-xs text-slate-500">Sessions</p>
                        </div>
                      </div>
                      <Link to={`/ai-curriculum/${childId}/${subject}`}>
                        <Button variant="outline" className="w-full">
                          Practice {subject}
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Languages - Temporarily hidden per product decision */}
            {false && overview?.languages?.enabled?.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-pink-500" />
                    Language Learning
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    {overview.languages.enabled.map(lang => (
                      <div key={lang} className="flex items-center gap-2 p-3 bg-pink-50 rounded-xl">
                        <span className="text-2xl">
                          {lang === 'spanish' ? '🇪🇸' : lang === 'french' ? '🇫🇷' : '🇬🇭'}
                        </span>
                        <div>
                          <p className="font-medium text-pink-800 capitalize">{lang}</p>
                          <p className="text-xs text-pink-600">Level {overview.languages.levels[lang] || 1}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* RECOMMENDATIONS TAB */}
          <TabsContent value="recommendations" className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  Personalized Recommendations
                </CardTitle>
                <CardDescription>
                  Based on {overview?.child_name}'s learning journey
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recommendations?.recommendations?.length > 0 ? (
                  <div className="space-y-4">
                    {recommendations.recommendations.map((rec, idx) => {
                      const RecIcon = iconMap[rec.icon] || Target;
                      const priorityColors = {
                        high: 'bg-red-50 border-red-200 text-red-800',
                        medium: 'bg-amber-50 border-amber-200 text-amber-800',
                        low: 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      };
                      
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={`p-4 rounded-xl border ${priorityColors[rec.priority]}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                              <RecIcon className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold">{rec.title}</h4>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {rec.priority}
                                </Badge>
                              </div>
                              <p className="text-sm opacity-80">{rec.description}</p>
                            </div>
                            <Link to={rec.action}>
                              <Button size="sm" variant="outline">
                                Go
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Trophy className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                    <p className="text-slate-600">Great job! No recommendations at this time.</p>
                    <p className="text-sm text-slate-400">Keep up the excellent work!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ParentProgressReport;
